import React from 'react';
import styled, { css } from 'styled-components';

import { convertThumbnailToPictureProps } from '@tager/web-modules';
import { isNotNullish, useMedia } from '@tager/web-core';

import Picture from '@/components/Picture';
import Button from '@/components/Button';
import { colors } from '@/constants/theme';
import { media } from '@/utils/mixin';
import CalendarIcon from '@/assets/svg/calendar.svg';

import CreditTags from './components/CreditTags';
import { CreditTag } from './components/CreditTags/types';
import CreditPaymentsSchedule from './components/CreditPaymentsSchedule';
import CreditInfo from './components/CreditInfo';
import { CreditCardProps } from './CreditCard.types';

function CreditCard({
  logo,
  name,
  description,
  firstPayment,
  lastPayment,
  monthPaymentHint,
  prepayment,
  period,
  periodDiscount,
  periodHint,
  currency,
  carPrice,
  isAlfaBankApproval,
  isBestOffer,
  onClick,
  onApplicationClick,
  onApprovalClick,
  onPaymentsScheduleClick,
  isActive,
}: CreditCardProps) {
  const isMobile = useMedia('(max-width: 767px)');

  const minPrepaymentFull = Math.round((prepayment * carPrice) / 100);

  const handleApplicationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    onApplicationClick?.();
  };

  const handleApprovalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onApprovalClick?.();
  };

  const handlePaymentsScheduleClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    onPaymentsScheduleClick?.();
  };

  const tags = (
    <CreditTagsWrapper>
      <CreditTags
        tags={
          [
            isBestOffer ? { tagType: 'best-offer' } : undefined,
            isAlfaBankApproval ? { tagType: 'alfabank-approval' } : undefined,
            currency ? { currency } : undefined,
          ].filter(isNotNullish) as CreditTag[]
        }
      />
    </CreditTagsWrapper>
  );

  return (
    <Component
      onClick={() => onClick?.()}
      isActive={isActive}
      isClickable={!!onClick}
    >
      <Content>
        <Top>
          <Header>
            <ImageLogo
              loading="lazy"
              {...convertThumbnailToPictureProps(logo)}
            />
            {!isMobile ? tags : <Name>{name}</Name>}
          </Header>

          {!isMobile ? <Name>{name}</Name> : tags}

          <Info>
            <CreditInfo
              title="Ежемесячный платеж"
              infoText={monthPaymentHint}
              firstPayment={firstPayment}
              lastPayment={lastPayment}
              firstPrefix="BYN"
            />

            <CreditInfo
              title="Срок"
              infoText={periodHint}
              value={periodDiscount ? periodDiscount : period}
              secondValue={period && !periodDiscount ? undefined : period}
              firstPrefix="мес"
              active
            />

            <CreditInfo
              title="Аванс"
              value={prepayment}
              secondValue={minPrepaymentFull}
              firstPrefix="%"
              secondPrefix="BYN"
            />
          </Info>

          {description && (
            <Description>
              {isMobile && <Title>Описание</Title>}

              <Text>{description}</Text>
            </Description>
          )}
        </Top>

        <Bottom>
          <CreditPaymentsScheduleWrapper>
            <CreditPaymentsSchedule
              title="График платежей"
              onClick={handlePaymentsScheduleClick}
              icon={<CalendarIcon />}
            />
          </CreditPaymentsScheduleWrapper>

          <Buttons>
            {onApprovalClick && (
              <StyledButton
                variant="outlined"
                size="small"
                onClick={handleApprovalClick}
              >
                Предодобрение
              </StyledButton>
            )}

            <StyledButton
              variant="contained"
              size="small"
              onClick={handleApplicationClick}
            >
              Подать заявку
            </StyledButton>
          </Buttons>
        </Bottom>
      </Content>
    </Component>
  );
}

export default CreditCard;

const Component = styled.div<{
  isActive: CreditCardProps['isActive'];
  isClickable: boolean;
}>`
  width: 100%;
  padding: 20px;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.gray1400};
  border-radius: 8px;

  ${media.mobile(
    css`
      padding: 12px;
    `
  )}

  ${({ isActive }) =>
    isActive &&
    css`
      border: 1px solid ${colors.orange200};
    `}

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: pointer;
    `}
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Top = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media.mobile(css`
    flex-direction: column;
    align-items: flex-start;
  `)}
`;

const ImageLogo = styled(Picture)`
  flex: 0 0 173px;
  width: 173px;
  height: 60px;
  padding: 5px;
  border-radius: 10px;
  background: ${colors.white};

  ${media.mobile(css`
    flex: auto;
    width: 160px;
    height: 48px;
  `)}

  img,
  picture {
    width: 100%;
    height: 100%;
    border-radius: 10px;
  }

  img {
    object-fit: contain;
  }
`;

const CreditTagsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  max-width: 500px;
  margin-left: 50px;

  ${media.mobile(css`
    margin: 10px 0 0;
    align-items: flex-start;
    justify-content: flex-start;
  `)}
`;

const Name = styled.p`
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  margin-top: 16px;
  color: ${(props) => props.theme.black100};

  ${media.mobile(css`
    font-size: 14px;
    line-height: 16px;
    margin-top: 5px;
  `)}
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 15px;

  ${media.mobile(css`
    grid-template-columns: repeat(1, 1fr);
    margin-top: 12px;
  `)}
`;

const Description = styled.div`
  margin-top: 16px;
`;

const Title = styled.span`
  font-size: 12px;
  line-height: 14px;
  color: ${(props) => props.theme.black200_alpha40};
`;

const Text = styled.p`
  font-weight: 300;
  font-size: 14px;
  line-height: 125%;
  margin-top: 10px;
  color: ${(props) => props.theme.black200};

  ${media.mobile(css`
    font-size: 12px;
    line-height: 16px;
    min-height: 96px;
  `)}
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;

  ${media.mobile(css`
    flex-direction: column-reverse;
    align-items: normal;
  `)}
`;

const CreditPaymentsScheduleWrapper = styled.div`
  ${media.mobile(css`
    display: flex;
    justify-content: center;
  `)}
`;

const Buttons = styled.div`
  display: flex;

  ${media.mobile(css`
    flex-direction: column;
    margin-bottom: 15px;
  `)}
`;

const StyledButton = styled(Button)`
  width: 160px;

  ${media.mobile(
    css`
      width: 100%;
    `
  )}

  &:not(:first-child) {
    margin-left: 15px;

    ${media.mobile(css`
      margin: 10px 0 0;
    `)}
  }
`;
